import { ref } from 'vue';

/**
 * Valida um CPF (11 dígitos) conferindo os dígitos verificadores.
 */
function validarCPF(valor) {
  const cpf = (valor || '').replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false; // rejeita sequências repetidas (000..., 111...)

  const calcularDigito = (qtd) => {
    let soma = 0;
    for (let i = 0; i < qtd; i++) {
      soma += parseInt(cpf.charAt(i), 10) * (qtd + 1 - i);
    }
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return (
    calcularDigito(9) === parseInt(cpf.charAt(9), 10) &&
    calcularDigito(10) === parseInt(cpf.charAt(10), 10)
  );
}

/**
 * Valida um CNPJ (14 dígitos) conferindo os dígitos verificadores.
 */
function validarCNPJ(valor) {
  const cnpj = (valor || '').replace(/\D/g, '');
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false; // rejeita sequências repetidas

  const calcularDigito = (tamanho) => {
    let soma = 0;
    let peso = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(cnpj.charAt(tamanho - i), 10) * peso--;
      if (peso < 2) peso = 9;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  return (
    calcularDigito(12) === parseInt(cnpj.charAt(12), 10) &&
    calcularDigito(13) === parseInt(cnpj.charAt(13), 10)
  );
}

/**
 * Composable para gerenciar a validação do formulário de reembolso.
 */
export function useReembolsoValidation() {
  const errosFormulario = ref({});
  const mostrarErroDocumentos = ref(false);
  const mostrarErroJustificativa = ref(false);

  const limparErros = () => {
    errosFormulario.value = {};
    mostrarErroDocumentos.value = false;
    mostrarErroJustificativa.value = false;
  };

  const validarEtapa2 = (dadosFormulario) => {
    let ehValido = true;
    if (!dadosFormulario.serviceType) {
      errosFormulario.value.serviceType = 'O tipo de serviço é obrigatório.';
      ehValido = false;
    }
    if (!dadosFormulario.providerName?.trim() && !dadosFormulario.providerNameManual?.trim()) {
      errosFormulario.value.providerNameManual = 'O nome do prestador é obrigatório.';
      ehValido = false;
    }
    const cpfCnpjLimpo = (dadosFormulario.providerCpfCnpj || '').replace(/\D/g, '');
    if (!cpfCnpjLimpo) {
      errosFormulario.value.providerCpfCnpj = 'O CPF/CNPJ do prestador é obrigatório.';
      ehValido = false;
    } else if (cpfCnpjLimpo.length <= 11) {
      if (!validarCPF(cpfCnpjLimpo)) {
        errosFormulario.value.providerCpfCnpj = 'CPF inválido. Confira os números digitados.';
        ehValido = false;
      }
    } else if (!validarCNPJ(cpfCnpjLimpo)) {
      errosFormulario.value.providerCpfCnpj = 'CNPJ inválido. Confira os números digitados.';
      ehValido = false;
    }
    if (!dadosFormulario.totalValue) {
      errosFormulario.value.totalValue = 'O valor total é obrigatório.';
      ehValido = false;
    }
    if (!dadosFormulario.expenseDate) {
      errosFormulario.value.expenseDate = 'A data da despesa é obrigatória.';
      ehValido = false;
    } else {
      const dataDespesa = new Date(dadosFormulario.expenseDate);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      if (dataDespesa > hoje) {
        errosFormulario.value.expenseDate = 'A data da despesa não pode ser uma data futura.';
        ehValido = false;
      }
    }
    if (!dadosFormulario.invoiceNumber?.trim()) {
      errosFormulario.value.invoiceNumber = 'O número do documento fiscal é obrigatório.';
      ehValido = false;
    }
    return ehValido;
  };

  const validarEtapa3 = (listaDocumentos, justificativa) => {
    let ehValido = true;
    if (listaDocumentos.some(doc => doc.required && (!doc.files || doc.files.length === 0))) {
      mostrarErroDocumentos.value = true;
      ehValido = false;
    }
    if (!justificativa?.trim()) {
      mostrarErroJustificativa.value = true;
      ehValido = false;
    }
    return ehValido;
  };

  const validarEtapa4 = (dadosFormulario, isManual) => {
    let ehValido = true;
    
    if (isManual) {
      if (!dadosFormulario.manualBankName?.trim()) {
        errosFormulario.value.manualBankName = 'O nome do banco é obrigatório.';
        ehValido = false;
      }
      if (!dadosFormulario.manualAgencyName?.trim()) {
        errosFormulario.value.manualAgencyName = 'O nome da agência é obrigatório.';
        ehValido = false;
      }
      if (!dadosFormulario.manualBankAccount?.trim()) {
        errosFormulario.value.manualBankAccount = 'A conta é obrigatória.';
        ehValido = false;
      }
    } else {
      if (!dadosFormulario.bankId) {
        errosFormulario.value.bankId = 'O banco é obrigatório.';
        ehValido = false;
      }
      if (!dadosFormulario.agencyId) {
        errosFormulario.value.agencyId = 'A agência é obrigatória.';
        ehValido = false;
      }
      if (!dadosFormulario.bankAccount?.trim()) {
        errosFormulario.value.bankAccount = 'A conta é obrigatória.';
        ehValido = false;
      }
    }
    return ehValido;
  };

  return {
    errosFormulario,
    mostrarErroDocumentos,
    mostrarErroJustificativa,
    limparErros,
    validarEtapa2,
    validarEtapa3,
    validarEtapa4,
  };
}